import { useAppDispatch } from 'app/hooks';
import { useAppContext } from 'providers/AppProvider';
import { useTranslation } from 'react-i18next';
import { removeMemberFromCurrentProject } from 'redux-slices/current-projects-slice';
import {
  addProject,
  removeProject,
  updateProject as updateProjectRedux,
  updateProjects
} from 'redux-slices/projects-slice';
import { ProjectPendingCounts } from 'redux-slices/project-pending-slice';
import { Project, ProjectMemberWithStats, ProjectStatus } from 'types/data/project.t';
import { UploadedFile, UploadedRelatedImage } from 'types/data/uploaded-file.t';
import { User } from 'types/data/user.t';

export type CreateProjectParams = Omit<
  Project,
  'id' | 'createdAt' | 'updatedAt' | 'archivedAt' | 'owner' | 'leads' | 'members'
> & {
  leadIds: number[];
  memberIds: number[];
};

export type UpdateProjectParams = Partial<CreateProjectParams>;

type expenseTotal = {
  id: number;
  name: string;
  unit: string;
  total: number;
};
export type ProjectStats = {
  doneTasksHours: number;
  doneHourCardsHours: number;
  expenses: expenseTotal[];
};

const useProjectData = () => {
  const { apiClient, errorNotification } = useAppContext();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  /**
   * Fetch a single project by ID (regardless of status)
   *
   * @param {number} projectId
   * @returns {Promise<Project>}
   *
   * @throws {ApiError}
   */
  const fetchProject = async (projectId: number): Promise<Project> => {
    try {
      return await apiClient.get<Project>(`/api/projects/${projectId}`);
    } catch (error) {
      if (error instanceof Error) {
        errorNotification(t('failedToLoad') + '\n' + error.message);
      }
      throw error;
    }
  };

  /**
   * Fetch projects from the API
   *
   * @param {ProjectFilters} filters
   * @returns {Promise<Project[]>}
   *
   * @throws {ApiError}
   */
  const fetchProjects = async (status: ProjectStatus, updateRedux = false): Promise<Project[]> => {
    try {
      const projectsWithCount = await apiClient.get<{ count: number; rows: Project[] }>('/api/projects', {
        status: status,
        withStats: false
      });
      const projects = projectsWithCount.rows;
      if (updateRedux) {
        dispatch(updateProjects(projects));
      }
      return projects;
    } catch (error) {
      if (error instanceof Error) {
        errorNotification(t('failedToLoad') + '\n' + error.message);
      }
      throw error;
    }
  };

  /**
   * Fetch projects with server-side pagination
   *
   * @param status ProjectStatus
   * @param withStats include stats
   * @param updateRedux whether to update redux store with returned items
   * @param params.pagination { page, limit, search }
   */
  const fetchProjectsPaged = async (
    status: ProjectStatus,
    withStats = false,
    params: { page?: number; limit?: number; search?: string } = {}
  ): Promise<{ rows: Project[]; count: number }> => {
    try {
      const { page = 1, limit = 8, search } = params;
      const result = await apiClient.get<{ rows: Project[]; count: number }>('/api/projects', {
        status: status,
        withStats: withStats,
        page,
        limit,
        search: search && search.length >= 3 ? search : undefined
      });
      return result;
    } catch (error) {
      if (error instanceof Error) {
        errorNotification(t('failedToLoad') + '\n' + error.message);
      }
      throw error;
    }
  };

  /**
   * Fetch project members
   *
   * @param {number} projectId
   * @param {boolean} tasks - Also include users with tasks in the project
   * @param {boolean} hourCards - Also include users with hour cards in the project
   * @param {boolean} withStats - Include per-member task/hour card counts, done hours and expense type count
   * @returns {Promise<ProjectMemberWithStats[]>}
   */
  const fetchProjectMembers = async (
    projectId: number,
    tasks: boolean = false,
    hourCards: boolean = false,
    withStats: boolean = false
  ): Promise<ProjectMemberWithStats[]> => {
    try {
      return await apiClient.get<ProjectMemberWithStats[]>(`/api/projects/${projectId}/members`, {
        tasks: tasks,
        hourCards: hourCards,
        withStats: withStats
      });
    } catch (error) {
      if (error instanceof Error) {
        errorNotification(t('failedToLoad') + '\n' + error.message);
      }
      throw error;
    }
  };

  /**
   * Fetch project files
   *
   * @param {number} projectId
   * @returns {Promise<UploadedFile[]>}
   */
  const fetchProjectFiles = async (projectId: number, order?: 'asc' | 'desc'): Promise<UploadedFile[]> => {
    try {
      const url = `/api/projects/${projectId}/files`;
      return await apiClient.get<UploadedFile[]>(url, {
        order: order
      });
    } catch (error) {
      if (error instanceof Error) {
        errorNotification(t('failedToLoad') + '\n' + error.message);
      }
      throw error;
    }
  };

  /**
   * Fetch project related images
   *
   * @param {number} projectId
   * @returns {Promise<UploadedRelatedImage[]>}
   */
  const fetchProjectRelatedImages = async (
    projectId: number,
    tasks: boolean,
    hourCards: boolean,
    order?: 'asc' | 'desc'
  ): Promise<UploadedRelatedImage[]> => {
    try {
      const url = `/api/projects/${projectId}/files-other`;
      return await apiClient.get<UploadedRelatedImage[]>(url, {
        tasks: tasks,
        hourCards: hourCards,
        order: order
      });
    } catch (error) {
      if (error instanceof Error) {
        errorNotification(t('failedToLoad') + '\n' + error.message);
      }
      throw error;
    }
  };

  /**
   * Get the count of active and archived projects
   *
   * @returns {Promise<{active: number, archived: number}>}
   *
   * @throws {ApiError}
   */
  const getProjectsCount = async (): Promise<{
    active: number;
    inactive: number;
    archived: number;
  }> => {
    try {
      return await apiClient.get<{ active: number; inactive: number; archived: number }>('/api/projects/count');
    } catch (error) {
      if (error instanceof Error) {
        errorNotification(t('failedToLoad') + '\n' + error.message);
      }
      throw error;
    }
  };

  /**
   * Get project files count
   *
   * @param {number} projectId
   *
   * @returns {Promise<number>}
   */
  const getProjectFilesCount = async (projectId: number): Promise<{ project: number; other: number }> => {
    try {
      return await apiClient.get<{ project: number; other: number }>(`/api/projects/${projectId}/files-count`);
    } catch (error) {
      if (error instanceof Error) {
        errorNotification(t('failedToLoad') + '\n' + error.message);
      }
      throw error;
    }
  };

  /**
   * Get project stats
   *
   * @param {number} projectId
   *
   * @returns {Promise<ProjectStats>}
   */
  const getProjectStats = async (projectId: number): Promise<ProjectStats> => {
    try {
      return await apiClient.get<ProjectStats>(`/api/projects/${projectId}/stats`);
    } catch (error) {
      if (error instanceof Error) {
        errorNotification(t('failedToLoad') + '\n' + error.message);
      }
      throw error;
    }
  };

  /**
   * Create a new project
   *
   * @param {Project} project
   * @returns {Promise<Project>}
   *
   * @throws {ApiError}
   */
  const createProject = async (project: CreateProjectParams): Promise<Project> => {
    try {
      const newProject = await apiClient.post<Project>('/api/projects', project);
      dispatch(addProject(newProject));
      return newProject;
    } catch (error) {
      if (error instanceof Error) {
        errorNotification(t('failedToSave') + '\n' + error.message);
      }
      throw error;
    }
  };

  /**
   * Update a project
   *
   * @param {number} projectId
   * @param {UpdateProjectParams} params
   * @returns {Promise<Project>}
   *
   * @throws {ApiError}
   */
  const updateProject = async (projectId: number, params: UpdateProjectParams): Promise<Project> => {
    try {
      const project = await apiClient.patch<Project>(`/api/projects/${projectId}`, params);
      dispatch(updateProjectRedux(project));
      return project;
    } catch (error) {
      if (error instanceof Error) {
        errorNotification(t('failedToSave') + '\n' + error.message);
      }
      throw error;
    }
  };

  /**
   * Add members to a project
   *
   * @param {number} projectId
   * @param {number[]}
   *
   * @returns {Promise<User[]>}
   */
  const addMembers = async (projectId: number, userIds: number[]): Promise<User[]> => {
    try {
      return await apiClient.post<User[]>(`/api/projects/${projectId}/members`, {
        memberIds: userIds
      });
    } catch (error) {
      if (error instanceof Error) {
        errorNotification(t('failedToSave') + '\n' + error.message);
      }
      throw error;
    }
  };

  /**
   * Change member role
   *
   * @param {number} projectId
   * @param {number} userId
   * @param {boolean} isLead
   */
  const changeMemberRole = async (
    projectId: number,
    userId: number,
    isLead: boolean
  ): Promise<{ userId: number; isLead: boolean }> => {
    try {
      return await apiClient.post(`/api/projects/${projectId}/members/change-role`, {
        userId,
        isLead
      });
    } catch (error) {
      if (error instanceof Error) {
        errorNotification(t('failedToSave') + '\n' + error.message);
      }
      throw error;
    }
  };

  /**
   * Delete a project
   *
   * @param {number} projectId
   * @returns {Promise<void>}
   *
   * @throws {ApiError}
   */
  const deleteProject = async (projectId: number): Promise<void> => {
    try {
      await apiClient.delete(`/api/projects/${projectId}`);
      dispatch(removeProject(projectId));
    } catch (error) {
      if (error instanceof Error) {
        errorNotification(t('failedToDelete') + '\n' + error.message);
      }
      throw error;
    }
  };

  /**
   * Delete a project member
   *
   * @param {number} projectId
   * @param {number} userId
   */
  const deleteProjectMember = async (projectId: number, userId: number): Promise<void> => {
    try {
      await apiClient.delete(`/api/projects/${projectId}/members/${userId}`);
      dispatch(removeMemberFromCurrentProject(userId));
    } catch (error) {
      if (error instanceof Error) {
        errorNotification(t('failedToDelete') + '\n' + error.message);
      }
      throw error;
    }
  };

  /**
   * Get project pending counts (waiting and rejected tasks/hour cards)
   *
   * @param {number} projectId
   *
   * @returns {Promise<ProjectPendingCounts>}
   */
  const getProjectPending = async (projectId: number): Promise<ProjectPendingCounts> => {
    try {
      return await apiClient.get<ProjectPendingCounts>(`/api/projects/${projectId}/pending`);
    } catch (error) {
      if (error instanceof Error) {
        errorNotification(t('failedToLoad') + '\n' + error.message);
      }
      throw error;
    }
  };

  /**
   * Get project hours chart data
   *
   * @param {number} projectId
   * @param {object} params Query parameters
   * @param {string} params.from Start date (YYYY-MM-DD)
   * @param {string} params.to End date (YYYY-MM-DD)
   * @param {'day' | 'week'} params.groupBy Group by day or week
   * @param {boolean} params.includeTasks Include tasks
   * @param {boolean} params.includeHourCards Include hour cards
   * @param {number | null} params.userId Filter by user ID (null for all)
   *
   * @returns {Promise<Array<{ label: string; hours: number }>>}
   */
  const getProjectHoursChart = async (
    projectId: number,
    params: {
      from: string;
      to: string;
      groupBy: 'day' | 'week';
      includeTasks: boolean;
      includeHourCards: boolean;
      userId?: number | null;
    }
  ): Promise<Array<{ date: string; hours: number }>> => {
    try {
      const queryParams: Record<string, string | number> = {
        from: params.from,
        to: params.to,
        groupBy: params.groupBy,
        includeTasks: params.includeTasks.toString(),
        includeHourCards: params.includeHourCards.toString()
      };

      if (params.userId !== null && params.userId !== undefined) {
        queryParams.userId = params.userId;
      }

      return (
        await apiClient.get<{ data: { date: string; hours: number }[] }>(
          `/api/projects/${projectId}/hours-chart`,
          queryParams
        )
      ).data;
    } catch (error) {
      if (error instanceof Error) {
        errorNotification(t('failedToLoad') + '\n' + error.message);
      }
      throw error;
    }
  };

  return {
    fetchProject,
    fetchProjects,
    fetchProjectsPaged,
    fetchProjectMembers,
    fetchProjectFiles,
    fetchProjectRelatedImages,
    getProjectsCount,
    getProjectFilesCount,
    getProjectStats,
    createProject,
    updateProject,
    addMembers,
    changeMemberRole,
    deleteProject,
    deleteProjectMember,
    getProjectPending,
    getProjectHoursChart
  };
};

export default useProjectData;
